import { Contact, UserSearchResult } from '../types';
import api from './api';
import {
  applySendPostageResults,
  getLocalContactLookup,
  getLocalOutboundPostage,
  mergeContactsIntoLocalStore,
  OutboundPostageSource,
  readContactLocalStore,
  setLocalContactLookup,
  setLocalIdentityNotFound,
  setLocalInboundRate,
  setLocalNickname,
  setLocalOutboundPostage,
  upsertContactInLocalStore,
} from './contactLocalStore';
import { cacheKeyFromAddress, normalizeQixitAddress } from '../utils/qixitAddress';

export type { OutboundPostageSource } from './contactLocalStore';
export { applySendPostageResults } from './contactLocalStore';

/** Hydrate session helpers from device store (no API). */
export function seedContactIdentityCache(username: string, contacts: Contact[]): void {
  mergeContactsIntoLocalStore(username, contacts);
}

export function setContactInboundRate(username: string, aliasAddress: string, deliveryRequired: number): void {
  setLocalInboundRate(username, aliasAddress, deliveryRequired);
}

export function setContactNickname(
  username: string,
  aliasAddress: string,
  nickname: string,
  aliasDisplayName?: string
): void {
  setLocalNickname(username, aliasAddress, nickname, aliasDisplayName);
}

export function setOutboundPostage(username: string, aliasAddress: string, requiredPostage: number): void {
  setLocalOutboundPostage(username, aliasAddress, requiredPostage, 'fetch');
}

export function getCachedNickname(username: string, aliasAddress: string): string {
  const normalized = normalizeQixitAddress(aliasAddress);
  if (!normalized) return '';
  const record = readContactLocalStore(username);
  const contact = record?.contacts.find(
    (c) => (c.address || '').trim().toLowerCase() === normalized
  );
  return (contact?.nickname || '').trim();
}

export function getCachedOutboundPostage(username: string, aliasAddress: string): number | undefined {
  return getLocalOutboundPostage(username, aliasAddress);
}

export function getCachedContactLookup(username: string, aliasAddress: string): UserSearchResult | undefined {
  return getLocalContactLookup(username, aliasAddress);
}

/** Live fetch — use only when seeding a newly added contact. */
export async function fetchOutboundPostageFromApi(
  username: string,
  recipientUsername: string
): Promise<number> {
  const data = await api.get<{ requiredPostage: number }>(
    `/api/contacts/required-postage/${encodeURIComponent(recipientUsername.trim())}`
  );
  const rate = Number(data?.requiredPostage);
  const value = Number.isFinite(rate) && rate >= 0 ? rate : 0;
  const normalized = normalizeQixitAddress(recipientUsername);
  if (normalized) {
    setLocalOutboundPostage(username, normalized, value, 'fetch');
  }
  return value;
}

/**
 * After adding a contact: fetch live outbound postage + profile lookup, persist locally,
 * and return the resolved rate for immediate UI update.
 */
export async function seedNewContactAfterAdd(
  ownerUsername: string,
  contact: Contact,
  recipientUsername: string
): Promise<{ contact: Contact; outboundRate: number }> {
  const alias = recipientUsername.trim();
  const [outboundRate, lookupResult] = await Promise.all([
    fetchOutboundPostageFromApi(ownerUsername, alias),
    fetchContactLookupFromApi(ownerUsername, alias),
  ]);
  upsertContactInLocalStore(ownerUsername, contact);
  return { contact, outboundRate };
}

export function resolveContactId(contact: Contact & { contactId?: number }): number | undefined {
  const id = contact.contactID ?? contact.contactId;
  return typeof id === 'number' && Number.isFinite(id) ? id : undefined;
}

/** Fetch profile lookup only when not already cached (message-triggered lazy sync). */
export async function ensureContactLookupCached(
  username: string,
  recipientUsername: string
): Promise<UserSearchResult | null> {
  const normalized = normalizeQixitAddress(recipientUsername);
  if (!normalized || !username?.trim()) return null;

  const existing = getLocalContactLookup(username, normalized);
  if (existing) return existing;

  return fetchContactLookupFromApi(username, recipientUsername).then((result) =>
    result.status === 'found' ? result.info : null
  );
}

export type ContactLookupFetchResult =
  | { status: 'found'; info: UserSearchResult }
  | { status: 'not_found' }
  | { status: 'error' };

/** Live fetch — use when seeding or explicitly refreshing a contact profile. */
export async function fetchContactLookupFromApi(
  username: string,
  recipientUsername: string
): Promise<ContactLookupFetchResult> {
  const normalized = normalizeQixitAddress(recipientUsername);
  try {
    const info = await api.get<UserSearchResult>(
      `/api/contacts/lookup/${encodeURIComponent(recipientUsername.trim())}`
    );
    if (normalized) {
      setLocalContactLookup(username, normalized, info);
    }
    return { status: 'found', info };
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404 && normalized) {
      setLocalIdentityNotFound(username, normalized, true);
      return { status: 'not_found' };
    }
    return { status: 'error' };
  }
}

/** Refresh outbound postage and profile lookup for one contact (user-initiated). */
export async function refreshContactFromApi(
  ownerUsername: string,
  recipientUsername: string
): Promise<{ outboundRate: number; lookup: ContactLookupFetchResult }> {
  const alias = recipientUsername.trim();
  const [outboundRate, lookup] = await Promise.all([
    fetchOutboundPostageFromApi(ownerUsername, alias),
    fetchContactLookupFromApi(ownerUsername, alias),
  ]);
  return { outboundRate, lookup };
}

/** Update local inbound postage from an inbox message payload (no API). */
export function syncInboundPostageFromInboxMessage(
  username: string,
  fromAddress: string,
  deliveryFee: number
): void {
  if (!username?.trim() || !fromAddress?.trim()) return;
  const rate = Number(deliveryFee);
  if (!Number.isFinite(rate) || rate < 0) return;
  setLocalInboundRate(username, fromAddress, rate);
}

export async function fetchSenderInboundRate(username: string, senderAddress: string): Promise<number> {
  const normalized = normalizeQixitAddress(senderAddress);
  if (!normalized) return 0;

  const record = readContactLocalStore(username);
  const localContact = record?.contacts.find(
    (c) => cacheKeyFromAddress(c.address) === normalized
  );
  if (localContact) {
    return localContact.deliveryRequired ?? 0;
  }

  const data = await api.get<{ deliveryRequired: number; senderAddress: string }>(
    '/api/contacts/sender-rate',
    { params: { senderAddress: normalized } }
  );
  const rate = Number(data?.deliveryRequired);
  const value = Number.isFinite(rate) ? rate : 0;
  setLocalInboundRate(username, normalized, value);
  return value;
}
